import * as React from 'react';
import { useState, useEffect } from 'react';
import { Avatar, Box, Button, Paper, Stack, Typography, CircularProgress, Divider } from '@mui/material';

import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import usersEndpoints from '../../../services/userServices';
import {
    BRAND, BRAND_GRADIENT, CARD_SX, ICON_TILE_SX, PRIMARY_BTN_SX, SECTION_LABEL_SX, TEXT, DIVIDER,
} from './styles';

const getInitials = (name) => (
    (name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
);

function StatusPill({ verified }) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                px: 0.9,
                py: 0.15,
                borderRadius: '999px',
                fontSize: 11,
                fontWeight: 700,
                color: verified ? '#067A24' : '#B42318',
                bgcolor: verified ? 'rgba(9,172,49,0.1)' : 'rgba(227,76,20,0.1)',
            }}
        >
            {verified
                ? <VerifiedIcon sx={{ fontSize: 12 }} />
                : <ErrorOutlineIcon sx={{ fontSize: 12 }} />}
            {verified ? 'Verified' : 'Not verified'}
        </Box>
    );
}

function DetailRow({ icon, label, status, children }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={ICON_TILE_SX}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography sx={{ ...SECTION_LABEL_SX, fontSize: 10 }}>
                        {label}
                    </Typography>
                    {status}
                </Stack>
                {children}
            </Box>
        </Stack>
    );
}

const smallOutlinedButton = {
    mt: 0.75,
    py: 0.25,
    px: 1.25,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: '8px',
    borderColor: '#FFD2BD',
    color: BRAND,
    '&:hover': { borderColor: BRAND, bgcolor: 'rgba(255,82,0,0.04)' },
};

function UserDetailsCard(props){
    const [userDetail, setuserDetail]  = useState({});
    const [message, setMessage] = useState(null);
    const [msgColor, setMsgColor] = useState(null);
    const [loader, setLoader] = useState({
        verifyEmail: "none",
        verifyPhone: "none"
    });

    const colors = {
        SUCCESS: "success.main",
        ERROR: "error"
    };

    // Sync only when the details object changes (e.g. credits update), and keep a
    // locally-sent verification mail flag so the "Verify email" button can't reappear.
    useEffect(() => {
        if(props.details){
            setuserDetail((prev) => ({
                ...props.details,
                mailSendStatus: Boolean(prev.mailSendStatus || props.details.mailSendStatus),
            }));
            if(props.details.mailSendStatus === true){
                setMessage("Verification Mail Sent");
                setMsgColor(colors.SUCCESS);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.details]);

    const sendVerificationMail = async () => {
        let updatedValue = { verifyEmail: "block" };
        setLoader({ ...loader, ...updatedValue });
        const mailSend = await usersEndpoints.sendVerificationMail({});
        updatedValue = { verifyEmail: "none" };
        setLoader({ ...loader, ...updatedValue });
        if(mailSend['success'] === true){
            setuserDetail({
                ...userDetail,
                ...{ "mailSendStatus": true }
            });
            setMessage("Verification Mail has been successfully sent");
            setMsgColor(colors.SUCCESS);
        }
        else{
            setMessage(mailSend['message']);
            setMsgColor(colors.ERROR);
        }
    }

    const valueSx = { fontSize: 14, fontWeight: 700, color: TEXT, wordBreak: 'break-word' };

    return(
        <Paper elevation={0} sx={CARD_SX}>
            {/* Brand banner with the avatar overlapping its bottom edge */}
            <Box sx={{ height: 88, background: BRAND_GRADIENT }} />
            <Box sx={{ px: 3, pb: 3, mt: '-44px', textAlign: 'center' }}>
                <Avatar
                    src={ userDetail.image || undefined }
                    alt={ "user profile" }
                    sx={{
                        width: 88,
                        height: 88,
                        mx: 'auto',
                        border: '4px solid #fff',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                        bgcolor: '#FFF0E8',
                        color: BRAND,
                        fontSize: 30,
                        fontWeight: 800,
                    }}
                >
                    { getInitials(userDetail.name) }
                </Avatar>
                <Typography sx={{ mt: 1.5, fontWeight: 800, fontSize: 18, color: TEXT }}>
                    { userDetail && userDetail.name ? userDetail.name : '' }
                </Typography>
                <Typography sx={{ color: '#6B7280', fontWeight: 600, fontSize: 13.5 }}>
                    { userDetail && userDetail.companyName ? userDetail.companyName : '' }
                </Typography>
            </Box>

            <Divider sx={{ borderColor: DIVIDER }} />

            <Stack spacing={2.25} sx={{ p: 3 }}>
                <DetailRow
                    icon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
                    label="Email address"
                    status={<StatusPill verified={Boolean(userDetail.emailStatus)} />}
                >
                    <Typography sx={valueSx}>
                        { userDetail && userDetail.email ? userDetail.email : 'N.A' }
                    </Typography>
                    {
                        userDetail.emailStatus === false && userDetail.mailSendStatus === false &&
                        <Button
                            variant='outlined'
                            size="small"
                            sx={smallOutlinedButton}
                            onClick={ sendVerificationMail }
                            endIcon={
                                <CircularProgress
                                    size={ "0.8rem" }
                                    sx={{ display: loader.verifyEmail }}
                                />
                            }
                        >
                            Verify email
                        </Button>
                    }
                    {
                        message &&
                        <Typography sx={{ fontSize: 12, mt: 0.5 }} color={ msgColor }>
                            { message }
                        </Typography>
                    }
                </DetailRow>

                <DetailRow
                    icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />}
                    label="Phone"
                    status={<StatusPill verified={Boolean(userDetail.phoneStatus)} />}
                >
                    <Typography sx={valueSx}>
                        { userDetail && userDetail.phone ? userDetail.phone : 'N.A' }
                    </Typography>
                    {
                        userDetail.phoneStatus === false && userDetail.phone &&
                        <Button
                            variant='outlined'
                            size="small"
                            sx={smallOutlinedButton}
                        >
                            Verify phone
                        </Button>
                    }
                </DetailRow>

                <DetailRow
                    icon={<PlaceOutlinedIcon sx={{ fontSize: 18 }} />}
                    label="Address"
                >
                    <Typography sx={valueSx}>
                        { userDetail && userDetail.address ? userDetail.address : 'N.A' }
                    </Typography>
                </DetailRow>
            </Stack>

            {/* Credits summary */}
            <Box sx={{ px: 3, pb: 3 }}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: '12px',
                        bgcolor: '#FFF7F2',
                        border: '1px solid #FFE3D4',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: BRAND_GRADIENT,
                            flexShrink: 0,
                        }}
                    >
                        <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...SECTION_LABEL_SX, fontSize: 10 }}>
                            Credits left
                        </Typography>
                        <Typography sx={{ fontSize: 22, fontWeight: 900, color: TEXT, lineHeight: 1.2 }}>
                            { userDetail && userDetail.credits ? userDetail.credits : 0 }
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        href={ '/#price_container' }
                        target={ '_blank' }
                        sx={{ ...PRIMARY_BTN_SX, px: 2 }}
                    >
                        Buy credits
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
}

export default UserDetailsCard;
